/**
 * Talent trees, loadout validation, the recommended (bot) build and named presets
 * (PLAN §11 "Talents"). Tree data lives in data/talents/.
 *
 * Gating. PLAN: "Nodes cost 1–3 points, and tiers unlock at 5 / 12 / 20 points spent", with tier 3
 * being the capstone row. Read so that all three numbers gate something and tier 1 is open from
 * the first point:
 *   - tier 2 nodes need  5 points spent in tier 1               (TALENT_TIER_GATES[1])
 *   - tier 3 nodes need 12 points spent in tiers 1–2            (TALENT_TIER_GATES[2])
 *   - a capstone needs  20 points spent in non-capstone nodes   (CAPSTONE_GATE)
 * So a capstone costs at least 23 points of the 30 a level-30 hero has. Tune the constants,
 * not the code. Separately, the rank tier caps the usable talent tier (`maxTier`, from
 * RankTierDef.talentTiers: Novice/Contender 2, Champion and up 3).
 */
import type { ClassId, Role } from '@keystone/sim';
import { ROLE_DEFAULT_CLASS } from './data';
import { LEVEL_CAP } from './leveling';
import { RECOMMENDED_ORDER, TALENT_PRESETS, TALENT_TREES } from './data/talents/index';
import type { TalentPresetDef, TalentPresetId } from './data/talents/index';
import type { LoadoutError, TalentId, TalentLoadout, TalentNode } from './types';

export { TALENT_PRESETS, TALENT_TREES, RECOMMENDED_ORDER } from './data/talents/index';
export type { TalentPresetDef, TalentPresetId } from './data/talents/index';

/** +field: the hero level cap is the levelling cap (leveling.ts LEVEL_CAP; 10 in Phase 1). */
export const MAX_HERO_LEVEL = LEVEL_CAP;
/** Points spent in LOWER tiers required to pick a node of tier 1 / 2 / 3. */
export const TALENT_TIER_GATES: readonly [number, number, number] = [0, 5, 12];
/** Points spent in non-capstone nodes required to pick a capstone. */
export const CAPSTONE_GATE = 20;

/** Capstones are the tier-3 nodes in the class's mutually exclusive `*.capstone` group. */
export const isCapstone = (n: TalentNode): boolean => n.tier === 3 && !!n.choiceGroup?.endsWith('.capstone');

/** All nodes of a class (empty for unknown classes). */
export function talentTree(classId: ClassId): readonly TalentNode[] {
  return TALENT_TREES[classId] ?? [];
}

const NODE_INDEX: ReadonlyMap<TalentId, TalentNode> = new Map(Object.values(TALENT_TREES).flatMap((t) => t.map((n) => [n.id, n] as const)));

export function talentNode(id: TalentId): TalentNode | undefined {
  return NODE_INDEX.get(id);
}

/** Total cost of the known nodes of `nodes` (unknown ids count 0). */
export function pointsSpent(nodes: readonly TalentId[]): number {
  return nodes.reduce((s, id) => s + (NODE_INDEX.get(id)?.cost ?? 0), 0);
}

/**
 * Validate a loadout. `available` = hero talent points; `maxTier` = talent tiers allowed by
 * rank (RankTierDef.talentTiers). Gates are checked against the whole loadout, so the order of
 * `loadout.nodes` does not matter. Returns [] when valid.
 */
export function validateLoadout(loadout: TalentLoadout, available: number, maxTier = 3): LoadoutError[] {
  const errors: LoadoutError[] = [];
  const seen = new Set<TalentId>();
  const nodes: TalentNode[] = [];
  for (const id of loadout.nodes) {
    if (seen.has(id)) {
      errors.push({ kind: 'duplicate', nodeId: id });
      continue;
    }
    seen.add(id);
    const n = NODE_INDEX.get(id);
    if (!n) errors.push({ kind: 'unknownNode', nodeId: id });
    else if (n.classId !== loadout.classId) errors.push({ kind: 'wrongClass', nodeId: id });
    else nodes.push(n);
  }
  nodes.sort((a, b) => a.tier - b.tier || a.col - b.col);
  const spentBelow = (tier: number): number => nodes.filter((n) => n.tier < tier).reduce((s, n) => s + n.cost, 0);
  const spentNonCapstone = nodes.filter((n) => !isCapstone(n)).reduce((s, n) => s + n.cost, 0);
  const groups = new Map<string, TalentId>();
  for (const n of nodes) {
    for (const p of n.prereqs) if (!seen.has(p) || NODE_INDEX.get(p)?.classId !== loadout.classId) errors.push({ kind: 'missingPrereq', nodeId: n.id, prereq: p });
    if (n.tier > maxTier) errors.push({ kind: 'rankLocked', nodeId: n.id, tier: n.tier });
    const needed = TALENT_TIER_GATES[n.tier - 1] ?? 0;
    const before = spentBelow(n.tier);
    if (before < needed) errors.push({ kind: 'tierLocked', nodeId: n.id, tier: n.tier, spentBefore: before, needed });
    else if (isCapstone(n) && spentNonCapstone < CAPSTONE_GATE)
      errors.push({ kind: 'tierLocked', nodeId: n.id, tier: n.tier, spentBefore: spentNonCapstone, needed: CAPSTONE_GATE });
    if (n.choiceGroup) {
      if (groups.has(n.choiceGroup)) errors.push({ kind: 'choiceConflict', nodeId: n.id, group: n.choiceGroup });
      else groups.set(n.choiceGroup, n.id);
    }
  }
  const spent = nodes.reduce((s, n) => s + n.cost, 0);
  if (spent > available) errors.push({ kind: 'overBudget', spent, available });
  return errors;
}

/**
 * Take nodes from a priority order while the loadout stays valid. Repeats passes until nothing
 * changes, so a node skipped for a gate is picked once enough lower-tier points are in. Then,
 * with `topUp`, spends what is left on the class's other non-capstone nodes in tree order (it
 * never adds a capstone the order did not ask for). Pure and deterministic.
 */
export function fillLoadout(classId: ClassId, order: readonly TalentId[], points: number, maxTier = 3, topUp = false): TalentId[] {
  const picked: TalentId[] = [];
  const take = (ids: readonly TalentId[]): void => {
    for (let changed = true; changed; ) {
      changed = false;
      for (const id of ids) {
        if (picked.includes(id)) continue;
        if (validateLoadout({ id: '', name: '', classId, nodes: [...picked, id] }, points, maxTier).length === 0) {
          picked.push(id);
          changed = true;
        }
      }
    }
  };
  take(order);
  if (topUp) take(talentTree(classId).filter((n) => !isCapstone(n) && !picked.includes(n.id)).map((n) => n.id));
  return picked;
}

/** Recommended loadout for a role (bots and new characters) at `points` hero talent points. */
export function defaultLoadout(role: Role, points: number, maxTier = 3, classId: ClassId = ROLE_DEFAULT_CLASS[role]): TalentLoadout {
  return { id: 'default', name: 'Recommended', classId, nodes: fillLoadout(classId, RECOMMENDED_ORDER[classId] ?? [], points, maxTier, true) };
}

/** The named presets of a class ("Trash", "Boss"). */
export function talentPresets(classId: ClassId): readonly TalentPresetDef[] {
  return TALENT_PRESETS[classId] ?? [];
}

/** A named preset trimmed to the points and tiers available. Throws on an unknown preset. */
export function presetLoadout(classId: ClassId, presetId: TalentPresetId, points: number, maxTier = 3): TalentLoadout {
  const p = talentPresets(classId).find((x) => x.id === presetId);
  if (!p) throw new Error(`no talent preset ${presetId} for class ${classId}`);
  return { id: p.id, name: p.name, classId, nodes: fillLoadout(classId, p.order, points, maxTier, true) };
}

/** Saved-loadout starter set for a character: Recommended plus every named preset. */
export function starterLoadouts(classId: ClassId, role: Role, points: number, maxTier = 3): TalentLoadout[] {
  return [defaultLoadout(role, points, maxTier, classId), ...talentPresets(classId).map((p) => presetLoadout(classId, p.id, points, maxTier))];
}
